import { env } from '@/constants/configs'

import type { IBranchOption, ITimeSlot } from '../types/schema'

const DAY_MINUTES = 24 * 60

/** "HH:MM" or "HH:MM:SS" -> minutes since midnight. */
export function toMinutes(time: string): number {
  const [hours = '0', minutes = '0'] = time.split(':')
  return Number(hours) * 60 + Number(minutes)
}

/** minutes since midnight -> "HH:MM", wrapping past midnight. */
export function toHHMM(minutes: number): string {
  const wrapped = ((minutes % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES
  const h = Math.floor(wrapped / 60)
  const m = wrapped % 60

  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

interface FilterArgs {
  slots: ITimeSlot[]
  branch: IBranchOption | null
  /** YYYY-MM-DD */
  date: string
  /** The current instant, injected so this stays pure and testable. Read as spa wall clock. */
  now?: Date
}

/**
 * Wall clock at the spa (`env.TZ`), whatever the visitor's zone — a browser-local `getHours()`
 * would shift the same-day cut by their offset. Legacy pinned moment to Asia/Bangkok (main.ts L15).
 */
function spaWallClock(instant: Date): { dateStr: string; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: env.TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(instant)

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '00'

  return {
    dateStr: `${get('year')}-${get('month')}-${get('day')}`,
    minutes: Number(get('hour')) * 60 + Number(get('minute'))
  }
}

/** Today on the spa clock, `YYYY-MM-DD`. Identical on the server and in any visitor's browser. */
export function spaToday(now: Date = new Date()): string {
  return spaWallClock(now).dateStr
}

/**
 * Narrows to the branch window (rolled forward past midnight), and on today to `now + 2 h` —
 * Laravel's `advance_min_time`. Vue cut at `+1 h`, offering slots Laravel then rejected.
 * Approximation from Vue: generic open/close times, as `filter` exposes no per-date hours.
 */
export function filterTimeSlots({
  slots,
  branch,
  date,
  now = new Date()
}: FilterArgs): ITimeSlot[] {
  if (!branch || !date) return []

  const openMinutes = toMinutes(branch.branch_open_time)
  let closeMinutes = toMinutes(branch.branch_close_time)
  if (closeMinutes <= openMinutes) closeMinutes += DAY_MINUTES

  let earliest = openMinutes

  const spaNow = spaWallClock(now)

  if (date === spaNow.dateStr) {
    earliest = Math.max(earliest, spaNow.minutes + 120)
  }

  return slots.filter((slot) => {
    const minutes = toMinutes(slot.time_start)

    // Also test the slot rolled into the next day, so overnight branches keep post-midnight slots.
    return [minutes, minutes + DAY_MINUTES].some(
      (candidate) => candidate >= earliest && candidate <= closeMinutes
    )
  })
}

/** "H:MM" (booking `hours`) or "HH:MM:SS" (`total_hours`) -> minutes. */
export function durationToMinutes(hours: string): number {
  return toMinutes(hours)
}

/** `2:00` / `02:00:00` -> `2 hrs`. A raw unit suffix would read "2:00 hrs". */
export function formatDuration(value: string): string {
  const minutes = durationToMinutes(value)
  if (!minutes) return ''

  const h = Math.floor(minutes / 60)
  const m = minutes % 60

  if (!h) return `${m} min`
  if (!m) return `${h} hr${h === 1 ? '' : 's'}`

  return `${h} hr${h === 1 ? '' : 's'} ${m} min`
}
